import type { StackIndex, StarfallEventKind } from "../types/game";
import { STACK_INDICES, STARFALL_EVENT_KINDS, STARFALL_EVENT_NAMES } from "../types/game";

/**
 * Optional table rules the host turns on before the deal. Unlike the deck
 * configuration these change how a turn is *judged*, so they live on GameState
 * and are mirrored in `services/game-server/internal/engine/houserules.go` —
 * a rules change means changing both (see CLAUDE.md).
 *
 * It is an object rather than a loose boolean because the next house rule is
 * cheaper to add than it is to plumb a second flag through setup, persistence,
 * the wire contract and the lobby.
 */
export interface HouseRules {
  /**
   * Vinny's Rule: a player down to their last card may only play it on the
   * tallest pile on the table (any of them, when several are tied).
   */
  vinnysRule: boolean;
  /**
   * Jourdan's Streaks: five turns in a row without taking a pile set a player's
   * deck alight, and they may spend that fire — once — to shut piles on ONE
   * chosen opponent's next turn.
   *
   * Needs an opponent to be worth anything: see `streaksApply`, which is what
   * every table smaller than that goes through rather than this flag alone.
   */
  jourdansStreaks: boolean;
  /**
   * Blue Shell Mode: the fire aims itself at whoever is winning.
   *
   * A sub-option of Jourdan's Streaks and meaningless without it — there is no
   * fire to aim. Nobody picks a target: spending a streak marks whoever is
   * closest to going out, and every player tied at that count except the spender
   * themselves. Only a player leading ALONE cannot spend one, having nobody left
   * to aim at once they are taken out of their own lead.
   *
   * Off by default, and it is the one house rule here that is off because it is
   * *harsher* rather than because it is unusual. Streaks as they stand are a
   * decision; this takes the decision away and points the whole table's fire at
   * one player, which is a different game and not everyone's.
   */
  blueShell: boolean;
  /**
   * Constellations (named Starfall in code, its first name): three or more
   * matching top cards, or four in a row, light a star, and a full meter
   * draws a table event. See `engine/starfall.ts` and docs/EVENTS.md.
   *
   * On by default, and the host's to choose like Blue Shell, on any deck: a
   * half deck can never show three of a kind, but it can show four in a row.
   */
  starfall: boolean;
  /**
   * The Constellations events the host has switched OFF; every other one is in
   * the event deck.
   *
   * A list of what is off rather than what is on, for the reason absent fields
   * read as their default everywhere here: a config stored before this existed,
   * or before an event was added, has not said no to anything - it was never
   * asked - so it must deal every event, the newest included. Optional for the
   * same reason; `normalizeHouseRules` always fills it in, in wire order, with
   * unknown kinds dropped. Every event off is Constellations off.
   */
  starfallOff?: StarfallEventKind[];
  /**
   * The Mercy Rule: a player who ends a turn half a deal behind the average
   * opponent bursts a third of the gap back onto the piles. See
   * `engine/mercy.ts`.
   *
   * On by default and the host's to choose, like Constellations. Needs an
   * opponent to mean anything: see `mercyApplies`.
   */
  mercyRule: boolean;
  /**
   * Drought: three piles taken on three turns in a row wrap a deck in mist, and
   * the next pile it takes only half comes home - the rest scatters onto the
   * other piles. See `engine/drought.ts`.
   *
   * On by default and the host's to choose, like the Mercy Rule. Needs an
   * opponent to mean anything: see `droughtApplies`.
   */
  droughtRule: boolean;
  /**
   * Burning Deck: a deck that stays on fire past its ignition - unspent, every
   * other turn from the seventh - burns its top card away as its turn begins.
   * See `engine/burningDeck.ts`.
   *
   * On by default and the host's to choose, like Drought. Rides on Streaks, so it
   * needs the table Streaks needs: see `burningDeckApplies`.
   */
  burningDeck: boolean;
}

/**
 * The table a host gets without touching anything.
 *
 * Vinny's Rule is on: a round that ends with somebody dumping their last card
 * wherever it fits ends without a moment worth watching, and the tallest-pile
 * restriction is what makes the final card a play rather than a formality.
 */
export const DEFAULT_HOUSE_RULES: HouseRules = { vinnysRule: true, jourdansStreaks: true, blueShell: false, starfall: true, starfallOff: [], mercyRule: true, droughtRule: true, burningDeck: true };

/**
 * Fills in a house-rules block that may be missing or partial.
 *
 * Every entry point takes untrusted or historical input — a saved round from
 * before this existed, a Firestore document written by an older server, a
 * hand-built test state — and a rule that isn't mentioned must come out as a
 * boolean rather than as `undefined`, which is falsy today and a crash the
 * moment anything iterates the block.
 *
 * "Not mentioned" resolves to that rule's OWN default, not to false. A table
 * config stored before Jourdan's Streaks existed has not said no to it — it was
 * never asked — and a default-on rule that arrives off for everyone who has
 * played before is off for exactly the players most likely to notice. So only a
 * literal `false` switches it off, just as only a literal `true` switches a
 * default-off rule on and a truthy stray value can't enable one.
 *
 * Blue Shell is forced off when Jourdan's Streaks is off, rather than being left
 * as stored. It is a sub-option — every rule it modifies belongs to the parent —
 * so "on, but the thing it modifies isn't running" is a state nothing downstream
 * should have to reason about, and a host who switches Streaks back on has not
 * silently re-armed something they last saw greyed out.
 */
export function normalizeHouseRules(raw: Partial<HouseRules> | null | undefined): HouseRules {
  const jourdansStreaks = raw?.jourdansStreaks !== false;
  const starfallOff = cleanStarfallOff(raw?.starfallOff);
  return {
    vinnysRule: raw?.vinnysRule !== false,
    jourdansStreaks,
    blueShell: jourdansStreaks && raw?.blueShell === true,
    // A table with every event switched off has nothing to draw: that is the
    // rule off, said in a roundabout way, and is read as it.
    starfall: raw?.starfall !== false && starfallOff.length < STARFALL_EVENT_KINDS.length,
    starfallOff,
    mercyRule: raw?.mercyRule !== false,
    droughtRule: raw?.droughtRule !== false,
    burningDeck: raw?.burningDeck !== false,
  };
}

/**
 * The events a host has switched off, as known kinds in wire order. Anything
 * else in the list - a kind from a newer client, a typo in a hand-made request,
 * a duplicate - is dropped, so a stale or hostile list can switch off only real
 * events and never re-orders the deck.
 */
function cleanStarfallOff(raw: readonly unknown[] | null | undefined): StarfallEventKind[] {
  if (!Array.isArray(raw)) return [];
  return STARFALL_EVENT_KINDS.filter((k) => raw.includes(k));
}

/** The events in this table's deck: every kind the host has not switched off, in wire order. */
export function enabledStarfallEvents(rules: HouseRules): StarfallEventKind[] {
  const off = rules.starfallOff ?? [];
  return STARFALL_EVENT_KINDS.filter((k) => !off.includes(k));
}

/**
 * The rules a real table is dealt under: Vinny's Rule and Jourdan's Streaks are
 * always on, and only Blue Shell, Starfall, the Mercy Rule, Drought and Burning Deck are the
 * host's to choose.
 *
 * Applied where a host's choice comes IN — the deal (`setupGame`) and a stored
 * table config — and deliberately NOT in `normalizeHouseRules`, which the move
 * code calls on every state it judges. Hand-built states (engine tests, the
 * tutorial, staged boards) turn the rules off to isolate what they are showing,
 * and must keep that ability. Mirrors `TableHouseRules` in houserules.go.
 */
export function tableHouseRules(raw: Partial<HouseRules> | null | undefined): HouseRules {
  return {
    ...normalizeHouseRules(raw),
    vinnysRule: true,
    jourdansStreaks: true,
    blueShell: raw?.blueShell === true,
  };
}

/** True when the table is worth describing as non-standard, i.e. set differently from the defaults. */
export function hasHouseRules(rules: HouseRules): boolean {
  return (
    rules.vinnysRule !== DEFAULT_HOUSE_RULES.vinnysRule ||
    rules.jourdansStreaks !== DEFAULT_HOUSE_RULES.jourdansStreaks ||
    rules.blueShell !== DEFAULT_HOUSE_RULES.blueShell ||
    rules.starfall !== DEFAULT_HOUSE_RULES.starfall ||
    (rules.starfall && (rules.starfallOff ?? []).length > 0) ||
    rules.mercyRule !== DEFAULT_HOUSE_RULES.mercyRule ||
    rules.droughtRule !== DEFAULT_HOUSE_RULES.droughtRule ||
    rules.burningDeck !== DEFAULT_HOUSE_RULES.burningDeck
  );
}

/**
 * Short label for the lobby/setup summary line. Empty when the table is the
 * ordinary one.
 *
 * It names the DIFFERENCE from a default table, not the rules in force, which is
 * why a rule that is on by default appears here only by its absence. A line that
 * said "Jourdan's Streaks" on every table would be printing a constant, and the
 * one thing a joiner needs to spot — a table that plays unusually — would be
 * hidden inside it.
 */
export function describeHouseRules(rules: HouseRules): string {
  const names: string[] = [];
  if (!rules.vinnysRule) names.push("No Vinny's Rule");
  if (!rules.jourdansStreaks) names.push("No Streaks");
  if (rules.blueShell) names.push("Blue Shell");
  if (!rules.starfall) names.push("No Constellations");
  else for (const kind of rules.starfallOff ?? []) names.push(`No ${STARFALL_EVENT_NAMES[kind]}`);
  if (!rules.mercyRule) names.push("No Mercy Rule");
  if (!rules.droughtRule) names.push("No Drought");
  if (!rules.burningDeck) names.push("No Burning Deck");
  return names.join(" · ");
}

/** The number of cards left on the last card that triggers Vinny's Rule. */
export const VINNYS_RULE_CARDS_LEFT = 1;

/** Consecutive turns without taking a pile that set a player's deck on fire. */
export const STREAK_TURNS_TO_IGNITE = 5;
/**
 * Seats a table needs before a streak can be SPENT (`streakSpendApplies`).
 *
 * The fire is only ever spent on somebody else, so a solo run has nobody to aim
 * it at. Two is therefore not a balance number, it is the point below which a
 * spend has no content. A solo deck can still catch fire, but only for Burning
 * Deck (`streaksApply`): without it the deck would light and stay lit for the
 * rest of the round, promising a move that does not exist.
 */
export const STREAK_MIN_PLAYERS = 2;

/**
 * Whether a deck's streak counts at this table at all - whether it can catch
 * fire.
 *
 * At a table, always with the rule. Solo, only under Burning Deck: a solo fire
 * has nobody to be spent on (`streakSpendApplies`), so the burn it costs is the
 * whole of what it means, and without that it would be a fire that promises a
 * move that does not exist.
 *
 * The seat count is part of the rule rather than something the setup screen
 * filters out on its own, because the flag survives the table it was set on: a
 * host who plays a five-hander and then a solo run carries a stored
 * `jourdansStreaks: true` into a table that cannot honour it. Asking here is the
 * only version of this that also holds for a saved round resumed from before
 * the seats changed.
 *
 * Takes the player count rather than a GameState so the online client can reach
 * the same answer from a PublicGameState, as with `canUseStreak`.
 */
export function streaksApply(rules: HouseRules, playerCount: number): boolean {
  return rules.jourdansStreaks && (playerCount >= STREAK_MIN_PLAYERS || rules.burningDeck);
}

/**
 * Whether a fire can be SPENT here: Streaks counting, and somebody else at the
 * table to aim it at. A solo deck burns (`streaksApply`) but never spends.
 */
export function streakSpendApplies(rules: HouseRules, playerCount: number): boolean {
  return streaksApply(rules, playerCount) && playerCount >= STREAK_MIN_PLAYERS;
}
/** Piles the FIRST player to target someone shuts on them. */
export const STREAK_FIRST_TARGET_LOCKS = 2;
/**
 * Piles each LATER player adds, targeting someone who is already marked.
 *
 * Deliberately smaller than the first: a whole table piling onto one player is
 * the position this rule most needs to stay survivable, so ganging up has
 * diminishing returns rather than linear ones.
 */
export const STREAK_EXTRA_TARGET_LOCKS = 1;
/**
 * The most piles one player may have shut on a single turn.
 *
 * Four is not a round number picked for feel: it is the largest count that
 * always leaves the player a pile to play on. Their locks all lift the moment
 * their turn ends, so nothing else at the table is constrained by it — but a
 * fifth lock would leave the player to act with no legal move at all.
 */
export const MAX_STREAK_LOCKS = 4;

/**
 * One pile shut on a player's turn by somebody's spent streak.
 *
 * `playerId` is the player being LOCKED OUT, not the one who spent the streak.
 * Locks are materialised at the start of that player's turn (the smallest piles
 * on the table as it then stands) and lifted the moment it ends, so at most one
 * player is ever constrained and the list is only non-empty during their turn.
 */
export interface StreakLock {
  playerId: string;
  stackIndex: StackIndex;
}

/**
 * A player who has been marked by one or more spent streaks and has not yet had
 * the turn that pays for it.
 *
 * `lockCount` is how many piles will shut when their turn begins — published so
 * every client can print it on their deck, which is the only warning they get.
 */
export interface StreakTarget {
  playerId: string;
  lockCount: number;
}

/** One seat's remaining cards, for the rules that compare players against each other. */
export interface DeckCount {
  playerId: string;
  cardsLeft: number;
}

/**
 * Whoever is closest to going out: the fewest cards left, and everyone level
 * with them.
 *
 * Players who are already OUT are not in the running. Since a round runs on past
 * the first player out (see `endTurn`), a table in its last lap always has a
 * 0-card seat on it, and counting that seat as the leader would point Blue Shell
 * at somebody who has finished — a fire spent marking an empty deck, on a player
 * who will never take another turn.
 *
 * A list rather than a single id because a tie is not a corner case here — it is
 * the ordinary state of an early round, where several players are still on their
 * dealt hand. Blue Shell marks all of them, which is the only answer that
 * doesn't need an arbitrary tiebreak deciding who takes the hit.
 *
 * Seat order is preserved (it takes an array, not a map) so both engines list
 * them identically; a Go map would iterate at random.
 *
 * An empty table has no leader, so callers never get a phantom id back.
 */
export function leadingPlayerIds(decks: readonly DeckCount[]): string[] {
  const inTheRound = decks.filter((d) => d.cardsLeft > 0);
  if (inTheRound.length === 0) return [];
  const fewest = Math.min(...inTheRound.map((d) => d.cardsLeft));
  return inTheRound.filter((d) => d.cardsLeft === fewest).map((d) => d.playerId);
}

/**
 * Who a Blue Shell spend would land on, from this spender's point of view: the
 * lead, minus the spender themselves.
 *
 * The subtraction is the whole rule. Being level with the lead is not being
 * ahead of it, so a spender tied at the front still has somebody the fire can
 * usefully chase — the players holding the same count, who are as close to going
 * out as they are. Only a player leading ALONE comes back with nothing, which is
 * the case the rule actually means to refuse.
 *
 * Whether those targets can still absorb a mark is a separate question
 * (`canTargetPlayer`), asked by callers, exactly as it is for an aimed spend.
 *
 * One helper rather than the same two lines in both engines and both play
 * screens: a client that thought the spender could not spend, over a server that
 * would have allowed it, is a burning deck that does nothing when tapped.
 */
export function blueShellTargetIds(decks: readonly DeckCount[], spenderId: string): string[] {
  return leadingPlayerIds(decks).filter((id) => id !== spenderId);
}

/**
 * Why a burning deck cannot be spent under Blue Shell Mode, or null if it can.
 *
 * Advisory, and client-only — there is no Go counterpart, the same way
 * `engine/missedMatches.ts` has none. The engine already refuses both of these
 * spends (`streakTargetIds` throws on each); what it cannot do is tell a player
 * holding a fire they have not tapped yet, and by the time a throw could arrive
 * the tap has been swallowed. The two stay in step by being the same two
 * conditions asked in the same order.
 *
 * It is Blue Shell's alone because an aimed spend never goes quiet: that tap
 * opens a picker, and a picker with nobody in it says so. Blue Shell has no
 * picker to say anything in, which is exactly why its deck looked broken.
 *
 * Asked only of a deck that is alight on its owner's turn — "there is no fire"
 * is not a refusal and has nothing to explain.
 */
export type BlueShellSpendBlock = "sole-leader" | "lead-capped";

export function blueShellSpendBlock(
  decks: readonly DeckCount[],
  spenderId: string,
  targets: readonly StreakTarget[],
): BlueShellSpendBlock | null {
  const aimedAt = blueShellTargetIds(decks, spenderId);
  if (aimedAt.length === 0) return "sole-leader";
  if (!aimedAt.some((id) => canTargetPlayer(targets, id))) return "lead-capped";
  return null;
}

/** True once this player's deck is alight. */
export function isDeckOnFire(streak: number): boolean {
  return streak >= STREAK_TURNS_TO_IGNITE;
}

/** The piles currently shut, in index order, read off the lock list. */
export function lockedStackIndices(locks: readonly StreakLock[]): StackIndex[] {
  return STACK_INDICES.filter((i) => locks.some((l) => l.stackIndex === i));
}

/** Locks already queued against this player, or 0 if nobody has targeted them. */
export function pendingLockCount(targets: readonly StreakTarget[], playerId: string): number {
  return targets.find((t) => t.playerId === playerId)?.lockCount ?? 0;
}

/**
 * What targeting a player who already carries `current` locks leaves them on.
 *
 * The first streak spent on someone is worth two piles and every later one is
 * worth a single extra, up to the cap — so a second attacker still hurts and a
 * fourth is nearly free.
 */
export function locksAfterTargeting(current: number): number {
  const added = current === 0 ? STREAK_FIRST_TARGET_LOCKS : STREAK_EXTRA_TARGET_LOCKS;
  return Math.min(current + added, MAX_STREAK_LOCKS);
}

/**
 * How many piles the spend that left a player owing `total` added to them -
 * the inverse of `locksAfterTargeting` along the only path a mark can grow by
 * (0, then 2, then 3, then 4). A `streak-target` event carries the total, which
 * is what the engine needs; the table wants to show what was just SENT, and a
 * second attacker sends one pile, not three.
 *
 * Client-side advisory with no Go counterpart, like `engine/missedMatches.ts`.
 */
export function locksAddedBy(total: number): number {
  let previous = 0;
  let current = 0;
  while (current < total) {
    previous = current;
    const next = locksAfterTargeting(current);
    if (next === current) break;
    current = next;
  }
  return current === total ? total - previous : total;
}

/** Whether this player can absorb another streak, i.e. isn't already at the cap. */
export function canTargetPlayer(targets: readonly StreakTarget[], playerId: string): boolean {
  return pendingLockCount(targets, playerId) < MAX_STREAK_LOCKS;
}

/**
 * Whether the player to act may spend a streak right now, given the rule and
 * their streak.
 *
 * Expressed over the streak alone rather than over a GameState for the same
 * reason `allowedStackIndices` is: the online client has to reach the same
 * answer the server will from a PublicGameState, which carries neither cards nor
 * decks. Whether any particular opponent can still be targeted is a separate
 * question — `canTargetPlayer` — because a spender with one un-capped opponent
 * left still has a move.
 */
export function canUseStreak(rules: HouseRules, streak: number): boolean {
  if (!rules.jourdansStreaks) return false;
  return isDeckOnFire(streak);
}

/**
 * Indices of the pile(s) holding the most cards. Always at least one entry —
 * five empty piles all tie on zero — so callers never have to handle "no
 * tallest pile".
 */
export function tallestStackIndices(stackCounts: readonly number[]): StackIndex[] {
  return tallestAmong(STACK_INDICES, stackCounts);
}

/** The tallest of a subset of piles. Same tie behavior: every pile level with the top. */
function tallestAmong(pool: readonly StackIndex[], stackCounts: readonly number[]): StackIndex[] {
  const tallest = Math.max(...pool.map((i) => stackCounts[i] ?? 0));
  return pool.filter((i) => (stackCounts[i] ?? 0) === tallest);
}

/**
 * The piles a spent streak shuts: the `count` smallest that aren't already shut.
 *
 * Ties break on the lower index, which is arbitrary but has to be *identically*
 * arbitrary in both engines — a server and a client that disagreed about which
 * of two equal piles closed would draw two different boards from one rule.
 */
export function smallestOpenStackIndices(
  stackCounts: readonly number[],
  alreadyLocked: readonly StackIndex[],
  count: number = STREAK_FIRST_TARGET_LOCKS,
): StackIndex[] {
  return STACK_INDICES.filter((i) => !alreadyLocked.includes(i))
    .sort((a, b) => (stackCounts[a] ?? 0) - (stackCounts[b] ?? 0) || a - b)
    .slice(0, count)
    // Back into index order once they are chosen. Which piles close is decided
    // by depth; the ORDER they are reported in is only ever read as a set, and
    // leaving it in depth order would make two engines that agree on the answer
    // disagree on the list.
    .sort((a, b) => a - b);
}

/**
 * Which piles a player may target with their turn, given the house rules, the
 * pile sizes, how many cards they are holding and which piles are shut.
 *
 * This is the whole of Vinny's Rule plus the streak locks, and it is
 * deliberately expressed over counts rather than over a GameState: the online
 * client has to reach the same answer from a PublicGameState, which carries pile
 * counts and deck counts but no cards.
 *
 * The two COMPOSE in one direction only: shut piles come off the table first,
 * and Vinny's Rule then picks the tallest of what is left. The other order
 * deadlocks — the tallest pile can perfectly well be a shut one, which would
 * leave the player to act with nowhere legal to go, and since a lock is released
 * only by its owner's next turn nothing would come along to break it.
 *
 * It only ever narrows *where* a card may go. Whether a guess or a free
 * placement is the legal action on the pile it picks is unchanged, and the two
 * can never contradict each other — a pile is either free-placeable or
 * guessable, so whatever this returns always offers exactly one of them.
 */
export function allowedStackIndices(
  rules: HouseRules,
  stackCounts: readonly number[],
  cardsInHand: number,
  locked: readonly StackIndex[] = [],
): StackIndex[] {
  const open = STACK_INDICES.filter((i) => !locked.includes(i));
  // MAX_STREAK_LOCKS guarantees a pile is open, but a hand-built or historical
  // state need not respect it, and "no legal pile at all" must never be an
  // answer this function can give.
  const pool = open.length > 0 ? open : [...STACK_INDICES];
  if (!rules.vinnysRule || cardsInHand !== VINNYS_RULE_CARDS_LEFT) return pool;
  return tallestAmong(pool, stackCounts);
}

/**
 * Whether Starfall is in force under these rules - on any deck.
 *
 * There used to be a deck floor here: a half deck holds two copies of each
 * value, so three matching tops cannot happen, and with triplets the only way
 * to light a star the meter could never fill. Staircases light stars too, and
 * a half deck shows four in a row as readily as any other, so the floor went.
 * Pass rules that have been through `normalizeHouseRules`, which turns the rule
 * off when every event is. Mirrors `StarfallApplies` in houserules.go.
 */
export function starfallApplies(rules: HouseRules): boolean {
  return rules.starfall && enabledStarfallEvents(rules).length > 0;
}
